import { useEffect, useState } from "react";
import styles from "./HomeScreen.module.css";
import api from "../../services/api";
import { ENDPOINTS } from "../../utils/endpoints";
import { parseHome } from "../../models/Home";
import useAuthStore from "../../store/authStore";

const HomeScreen = () => {
  const [homeSections, setHomeSections] = useState([]);
  const [isLoading, setIsLoading] = useState(true);
  const [isDarkMode, setIsDarkMode] = useState(
    document.documentElement.getAttribute("data-theme") === "dark"
  );
  const { user } = useAuthStore();

  useEffect(() => {
    fetchHomeSections();

    // Listen for theme changes
    const observer = new MutationObserver(() => {
      setIsDarkMode(
        document.documentElement.getAttribute("data-theme") === "dark"
      );
    });

    observer.observe(document.documentElement, {
      attributes: true,
      attributeFilter: ["data-theme"],
    });

    return () => observer.disconnect();
  }, []);

  const fetchHomeSections = async () => {
    try {
      setIsLoading(true);
      const { data } = await api.get(ENDPOINTS.GET_ALL_HOME_SECTIONS);

      if (data.success) {
        const sections = data.sections.map(parseHome);
        setHomeSections(sections);
      }
    } catch (err) {
      console.error("Failed to fetch home sections", err);
    } finally {
      setIsLoading(false);
    }
  };

  // Filter only active sections
  const activeSections = homeSections.filter((section) => section.isActive);

  const parseColor = (colorString) => {
    if (!colorString) return undefined;
    return colorString;
  };

  const parseAlignment = (alignment) => {
    switch (alignment?.toLowerCase()) {
      case "left":
        return "left";
      case "center":
        return "center";
      case "right":
        return "right";
      default:
        return "left";
    }
  };

  const parseButtonAlignment = (alignment) => {
    switch (alignment?.toLowerCase()) {
      case "left":
        return "flex-start";
      case "center":
        return "center";
      case "right":
        return "flex-end";
      default:
        return "flex-start";
    }
  };

  const handleButtonClick = async (link) => {
    if (!link) return;

    try {
      const url = new URL(link);

      if (url.protocol === "http:" || url.protocol === "https:") {
        const isWordPressSite =
          url.hostname.includes("ecolearnacademy.com") ||
          url.hostname.includes("alphauniverseproject.com");

        if (isWordPressSite) {
          const userEmail = user?.email;

          if (userEmail) {
            try {
              const { data } = await api.post(ENDPOINTS.WP_AUTO_LOGIN, {
                target_url: link,
                email: userEmail,
              });

              if (data.success && data.login_url) {
                window.open(data.login_url, "_blank");
                return;
              }
            } catch (err) {
              console.error("Error getting auto-login URL:", err);
            }
          }
        }

        window.open(link, "_blank");
      } else {
        window.location.href = link;
      }
    } catch (err) {
      console.error("Invalid link:", err);
    }
  };

  const buildRichText = (text) => {
    // Split by newlines first
    const lines = text.split("\n");
    const elements = [];
    let keyCounter = 0;

    lines.forEach((line, lineIndex) => {
      const regex = /\*\*(.*?)\*\*/g;
      const parts = [];
      let lastIndex = 0;

      const matches = line.matchAll(regex);

      for (const match of matches) {
        const offset = match.index;

        if (offset > lastIndex) {
          parts.push(
            <span key={`text-${keyCounter++}`}>
              {line.substring(lastIndex, offset)}
            </span>
          );
        }

        parts.push(<strong key={`bold-${keyCounter++}`}>{match[1]}</strong>);

        lastIndex = offset + match[0].length;
      }

      if (lastIndex < line.length) {
        parts.push(
          <span key={`text-${keyCounter++}`}>{line.substring(lastIndex)}</span>
        );
      }

      // Add the line content
      if (parts.length > 0) {
        elements.push(<span key={`line-${lineIndex}`}>{parts}</span>);
      } else {
        elements.push(<span key={`line-${lineIndex}`}>{line}</span>);
      }

      // Add line break if not the last line
      if (lineIndex < lines.length - 1) {
        elements.push(<br key={`br-${lineIndex}`} />);
      }
    });

    return elements.length > 0 ? elements : <span>{text}</span>;
  };

  if (isLoading && homeSections.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.loadingContainer}>
          {[1, 2, 3].map((i) => (
            <div key={i} className={styles.shimmerItem}>
              <div className={styles.shimmerImage} />
              <div className={styles.shimmerTitle} />
              <div className={styles.shimmerBody} />
              <div className={styles.shimmerButton} />
            </div>
          ))}
        </div>
      </div>
    );
  }

  if (activeSections.length === 0) {
    return (
      <div className={styles.container}>
        <div className={styles.emptyState}>
          <div className={styles.emptyIcon}>🏠</div>
          <p>No content available</p>
        </div>
      </div>
    );
  }

  return (
    <div className={styles.container}>
      <div className={styles.sectionsContainer}>
        {activeSections.map((section) => {
          const titleTextColor = isDarkMode
            ? parseColor(section.titleTextColorDark) || "#ffffff"
            : parseColor(section.titleTextColorLight) || "#000000";

          const bodyTextColor = isDarkMode
            ? parseColor(section.bodyTextColorDark) || "#ffffffb3"
            : parseColor(section.bodyTextColorLight) || "#000000de";

          return (
            <div key={section.id} className={styles.section}>
              {/* Image at the top - Use website image if available, fallback to mobile */}
              {(section.websiteImageUrl || section.imageUrl) && (
                <div className={styles.imageContainer}>
                  <img
                    src={`${ENDPOINTS.IMAGE_BASE_URL}${
                      section.websiteImageUrl || section.imageUrl
                    }?t=${section.updatedAt}`}
                    alt={section.title}
                    className={styles.sectionImage}
                    loading="lazy"
                  />
                </div>
              )}

              {/* Content below image */}
              <div className={styles.contentWrapper}>
                {/* Title */}
                {section.title && (
                  <div
                    className={styles.titleContainer}
                    style={{
                      backgroundColor: parseColor(section.titleBackgroundColor),
                      textAlign: parseAlignment(section.titleAlignment),
                    }}
                  >
                    <h2 style={{ color: titleTextColor }}>
                      {buildRichText(section.title)}
                    </h2>
                  </div>
                )}

                {/* Body Text */}
                {section.bodyText && (
                  <div
                    className={styles.bodyContainer}
                    style={{
                      backgroundColor: parseColor(section.bodyBackgroundColor),
                      textAlign: parseAlignment(section.bodyTextAlignment),
                    }}
                  >
                    <p style={{ color: bodyTextColor }}>
                      {buildRichText(section.bodyText)}
                    </p>
                  </div>
                )}

                {/* Button */}
                {section.buttonText && (
                  <div
                    className={styles.buttonWrapper}
                    style={{
                      justifyContent: parseButtonAlignment(
                        section.buttonAlignment
                      ),
                    }}
                  >
                    <button
                      className={styles.actionButton}
                      onClick={() => handleButtonClick(section.buttonLink)}
                    >
                      {section.buttonText}
                    </button>
                  </div>
                )}
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
};

export default HomeScreen;
